import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../features/auth/context';
import { useToast } from '../hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { getAdminLlmRequests, AdminLlmRequest, LlmRequestType } from '../features/admin/api';
import Table from '../components/ui/table';
import type { Column } from '../components/ui/table';
import Button from '../components/ui/button';
import AdminCsvExportButton from '../features/admin/AdminCsvExportButton';

// Admin subpage: browse LLMRequest audit rows (what each AI call was for, which
// model + params, tokens, timing, and the full prompt/response behind a dialog).
// Backend: GET /api/admin/llm-requests. Data source: llm_requests table.
// Direct-DB alternative + schema: docs/integrations/openai.md.

const TYPE_FILTERS: { key: LlmRequestType | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'journey_generation', label: 'Generation' },
  { key: 'leg_pricing', label: 'Pricing' },
  { key: 'stop_details', label: 'Stop details' },
  { key: 'journey_edit', label: 'Edit' },
];

// Subtle per-type chip colors (readable in light + dark via alpha backgrounds).
const TYPE_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  journey_generation: { bg: 'rgba(59,130,246,0.15)', fg: '#2563eb', label: 'Generation' },
  leg_pricing: { bg: 'rgba(139,92,246,0.15)', fg: '#7c3aed', label: 'Pricing' },
  stop_details: { bg: 'rgba(20,184,166,0.15)', fg: '#0d9488', label: 'Stop details' },
  journey_edit: { bg: 'rgba(245,158,11,0.18)', fg: '#b45309', label: 'Edit' },
};

const Chip: React.FC<{ bg: string; fg: string; children: React.ReactNode }> = ({ bg, fg, children }) => (
  <span style={{ background: bg, color: fg, borderRadius: 999, padding: '2px 8px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

function typeChip(type: string | null) {
  if (!type) return <Chip bg="rgba(148,163,184,0.18)" fg="#64748b">unknown</Chip>;
  const s = TYPE_STYLE[type];
  return <Chip bg={s ? s.bg : 'rgba(148,163,184,0.18)'} fg={s ? s.fg : '#64748b'}>{s ? s.label : type}</Chip>;
}

function statusChip(status: string, errorType: string | null) {
  const ok = status === 'success';
  const pending = status === 'pending';
  const bg = ok ? 'rgba(34,197,94,0.15)' : pending ? 'rgba(245,158,11,0.18)' : 'rgba(239,68,68,0.15)';
  const fg = ok ? '#16a34a' : pending ? '#b45309' : '#dc2626';
  return <Chip bg={bg} fg={fg}>{ok || pending ? status : (errorType || 'error')}</Chip>;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '–';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

// Pretty-print JSON strings/objects; fall back to the raw string for plain text
// (e.g. the prompt) or invalid JSON.
function pretty(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  const str = String(value);
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
}

interface ViewerState { title: string; subtitle?: string; content: string }

const LogViewerDialog: React.FC<{ viewer: ViewerState; onClose: () => void }> = ({ viewer, onClose }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = 'unset'; };
  }, [onClose]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(viewer.content); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* no-op */ }
  };

  const content = (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}
    >
      <div
        role="dialog"
        aria-label={viewer.title}
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 10, width: '100%', maxWidth: 820, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontWeight: 700 }}>{viewer.title}</div>
            {viewer.subtitle && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{viewer.subtitle}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>
        </div>
        <pre style={{ margin: 0, padding: 16, overflow: 'auto', fontSize: 12.5, lineHeight: 1.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {viewer.content}
        </pre>
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
};

// Small "View" link-button for a JSON/text field; renders a dim dash when empty.
const FieldButton: React.FC<{ label: string; empty: boolean; onClick: () => void }> = ({ label, empty, onClick }) =>
  empty ? (
    <span style={{ color: 'var(--text-muted)' }}>–</span>
  ) : (
    <button
      onClick={onClick}
      style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 8px', fontSize: 12, cursor: 'pointer', color: 'var(--text)' }}
    >
      {label}
    </button>
  );

const AdminLlmRequestsPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState<AdminLlmRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<LlmRequestType | 'all'>('all');
  const [viewer, setViewer] = useState<ViewerState | null>(null);

  useEffect(() => {
    if (!authLoading && (!user || !user.isAdmin)) {
      showToast({ title: 'Access Denied', message: 'Admin access required. Redirecting to homepage.', type: 'error' });
      navigate('/');
    }
  }, [authLoading, user, navigate, showToast]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminLlmRequests(filter === 'all' ? {} : { type: filter });
      setRows(data);
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Failed to load LLM requests.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filter, showToast]);

  useEffect(() => { load(); }, [load]);

  const columns: Column<AdminLlmRequest>[] = useMemo(() => [
    { key: 'requestType', header: 'Type', render: (r) => typeChip(r.requestType) },
    { key: 'createdAt', header: 'When', render: (r) => <span title={new Date(r.createdAt).toLocaleString()}>{formatRelative(r.createdAt)}</span> },
    {
      key: 'model', header: 'Provider · Model',
      render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{r.provider}{r.model ? ` · ${r.model}` : ''}</span>,
    },
    { key: 'status', header: 'Status', render: (r) => statusChip(r.status, r.errorType) },
    { key: 'tokensUsed', header: 'Tokens', className: 'text-right', render: (r) => (r.tokensUsed ?? '–') },
    { key: 'durationMs', header: 'Duration', className: 'text-right', render: (r) => formatDuration(r.durationMs) },
    {
      key: 'journeyId', header: 'Journey',
      render: (r) => (r.journeyId ? <span title={r.journeyId} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.journeyId.slice(0, 8)}…</span> : <span style={{ color: 'var(--text-muted)' }}>–</span>),
    },
    {
      key: 'details', header: 'Details',
      render: (r) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <FieldButton label="Prompt" empty={!r.promptSent} onClick={() => setViewer({ title: 'Prompt', subtitle: `${typeLabel(r.requestType)}${r.model ? ` · ${r.model}` : ''}`, content: pretty(r.promptSent) })} />
          <FieldButton label="Response" empty={!r.responseFull} onClick={() => setViewer({ title: 'Response', subtitle: `${typeLabel(r.requestType)}${r.model ? ` · ${r.model}` : ''}`, content: pretty(r.responseFull) })} />
          <FieldButton label="Params" empty={r.requestParams == null} onClick={() => setViewer({ title: 'Request params', subtitle: `${typeLabel(r.requestType)}${r.model ? ` · ${r.model}` : ''}`, content: pretty(r.requestParams) })} />
        </div>
      ),
    },
  ], []);

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>LLM Requests</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={() => navigate('/admin')}>← Admin</Button>
          <Button variant="secondary" onClick={load} disabled={loading}>Refresh</Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {TYPE_FILTERS.map((f) => (
          <Button key={f.key} variant={filter === f.key ? 'primary' : 'secondary'} onClick={() => setFilter(f.key)}>
            {f.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-600">Loading LLM requests…</div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-sm text-slate-600">{rows.length} request(s) · newest first (latest 100)</div>
            <AdminCsvExportButton table="llm-requests" label="LLM requests" />
          </div>
          <div className="table-compact">
            <Table columns={columns} data={rows} emptyMessage="No LLM requests found." />
          </div>
        </>
      )}

      {viewer && <LogViewerDialog viewer={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
};

function typeLabel(type: string | null): string {
  if (!type) return 'unknown';
  return TYPE_STYLE[type] ? TYPE_STYLE[type].label : type;
}

export default AdminLlmRequestsPage;
